import {
  createContext,
  useContext,
  useEffect,
  useState,
  type ReactNode,
} from 'react';
import type { ReasonerAgentConfig } from '@/interfaces/reasoner.interface';
import { AgentMode } from '@/utils/agentMode.utils';
import { useProjectContext } from '@/context/ProjectContext';
import {
  getReasonerModelOption,
  reasonerDefaultModel,
  reasonerModels,
} from '@/config/models.config';

interface AgentConfigContextType {
  agentConfig: ReasonerAgentConfig;
  setAgentConfig: (config: ReasonerAgentConfig) => void;
  setRawAgentConfig: (config: ReasonerAgentConfig) => void;
  resetPlanMode: () => void;
}

function getDefaultAgentConfig(model?: string): ReasonerAgentConfig {
  const selectedModel =
    model || reasonerDefaultModel || reasonerModels[0]?.value || '';
  const opt = getReasonerModelOption(selectedModel);
  return {
    agent: 'basic',
    model: selectedModel,
    mode: AgentMode.ANALYZE,
    perspective: '',
    subagent: opt.subagent,
    compaction: opt.compaction,
    tool_max_tokens: opt.tool_max_tokens,
    metadata: opt.metadata,
  };
}

const STORAGE_KEY_PREFIX = 'agentConfig';

const REQUIRED_KEYS: (keyof ReasonerAgentConfig)[] = [
  'agent',
  'model',
  'subagent',
  'compaction',
  'tool_max_tokens',
  'metadata',
];

const AgentConfigContext = createContext<AgentConfigContextType | undefined>(
  undefined,
);

function getStorageKey(projectId: string | null): string {
  return projectId
    ? `${STORAGE_KEY_PREFIX}_${projectId}`
    : `${STORAGE_KEY_PREFIX}_global`;
}

function isValidStored(
  parsed: Partial<ReasonerAgentConfig>,
): boolean {
  return REQUIRED_KEYS.every((key) => key in parsed);
}

function getStoredConfig(projectId: string | null): ReasonerAgentConfig {
  try {
    const stored = localStorage.getItem(getStorageKey(projectId));
    if (stored) {
      const parsed = JSON.parse(stored) as Partial<ReasonerAgentConfig>;
      if (!isValidStored(parsed)) {
        localStorage.removeItem(getStorageKey(projectId));
        return getDefaultAgentConfig();
      }
      const model =
        parsed.model || reasonerDefaultModel || reasonerModels[0]?.value || '';
      const opt = getReasonerModelOption(model);
      return {
        agent: parsed.agent ?? 'basic',
        model,
        mode: parsed.mode ?? AgentMode.ANALYZE,
        perspective: parsed.perspective ?? '',
        subagent: opt.subagent,
        compaction: opt.compaction,
        tool_max_tokens: opt.tool_max_tokens,
        metadata: opt.metadata,
        debug: parsed.debug,
      };
    }
  } catch (error) {
    console.error('Failed to parse stored agent config:', error);
  }
  return getDefaultAgentConfig();
}

function storeConfig(
  config: ReasonerAgentConfig,
  projectId: string | null,
): void {
  try {
    localStorage.setItem(getStorageKey(projectId), JSON.stringify(config));
  } catch (error) {
    console.error('Failed to store agent config:', error);
  }
}

export function AgentConfigProvider({ 
  children,
  retoolDefaultModel,
}: { 
  children: ReactNode;
  retoolDefaultModel?: string;
}) {
  const { selectedProjectId } = useProjectContext();


  const [agentConfig, setAgentConfigState] = useState<ReasonerAgentConfig>(() => {
    // In Retool mode, always use default config with model preset
    if(retoolDefaultModel){
      return getDefaultAgentConfig(retoolDefaultModel);
    }
    return getStoredConfig(selectedProjectId);
  }   
  );
  
  const setAgentConfig = (config: ReasonerAgentConfig) => {
    const model = retoolDefaultModel || config.model;
    const opt = getReasonerModelOption(model);
    const nextConfig: ReasonerAgentConfig = {
      ...config,
      model,
      subagent: opt.subagent,
      compaction: opt.compaction,
      tool_max_tokens: opt.tool_max_tokens,
      metadata: opt.metadata,
    };
    setAgentConfigState(nextConfig);
    storeConfig(nextConfig, selectedProjectId);
  };

  const setRawAgentConfig = (config: ReasonerAgentConfig): void => {
    setAgentConfigState(config);
    storeConfig(config, selectedProjectId);
  };

  const resetPlanMode = () => {
    const newConfig: ReasonerAgentConfig = {
      ...agentConfig,
      mode: AgentMode.ANALYZE,
    };
    setAgentConfigState(newConfig);
    storeConfig(newConfig, selectedProjectId);
  };

  useEffect(() => {
    // In Retool mode, always use default config with model preset
    if(retoolDefaultModel){
      setAgentConfigState(getDefaultAgentConfig(retoolDefaultModel));
      return;
    }
    const stored = getStoredConfig(selectedProjectId);
    setAgentConfigState(stored);
  }, [selectedProjectId, retoolDefaultModel]);

  return (
    <AgentConfigContext.Provider
      value={{ agentConfig, setAgentConfig, setRawAgentConfig, resetPlanMode }}
    >
      {children}
    </AgentConfigContext.Provider>
  );
}

export function useAgentConfig() {
  const context = useContext(AgentConfigContext);
  if (context === undefined) {
    throw new Error('useAgentConfig must be used within an AgentConfigProvider');
  }
  return context;
}